import {
  useGetQuery,
  useLazyGetQuery,
  usePostMutation,
  usePutMutation,
} from "slices/api";
import {
  DEMAND_ESTIMATE,
  APPLICATION_DETAILS,
  OTHER_APPLICATION_DETAILS,
  TECHNICAL_SANCTION,
} from "tagTypes";

export const usePostDemandEstimationMutation = () => {
  const [demandEstimation, data] = usePostMutation();
  const addDemandEstimation = async (payload, applicationId, query = {}) => {
    return await demandEstimation({
      endpoint: `/application/${applicationId}/demand-estimate`,
      query,
      payload: payload || {},
      toastMessage: "Demand Estimation added successfully",
      tags: [DEMAND_ESTIMATE, APPLICATION_DETAILS],
    }).unwrap();
  };
  return [addDemandEstimation, data];
};

export const usePostDemandEstimationForOtherUtilityMutation = () => {
  const [demandEstimation, data] = usePostMutation();
  const addDemandEstimation = async (applicationId, query = {}) => {
    return await demandEstimation({
      endpoint: `/other-utility-application/${applicationId}/demand-estimate`,
      query,
      toastMessage: "Demand Estimation added successfully",
      tags: [DEMAND_ESTIMATE, APPLICATION_DETAILS],
    }).unwrap();
  };
  return [addDemandEstimation, data];
};

export const useGetDisconnectionChargesQuery = (applicationId) =>
  useGetQuery({
    endpoint: `/application/${applicationId}/get-disconnection-charge`,
    tags: [APPLICATION_DETAILS],
  });

export const useGetPendingDemandEstimateApprovalApplicationsData = (
  query = {}
) =>
  useGetQuery({
    endpoint: "/power/application/pending-demand-estimate-approval",
    query,
    tags: [APPLICATION_DETAILS, DEMAND_ESTIMATE],
  });

export const useGetDemandEstimationByApplicationIdQuery = (applicationId) =>
  useGetQuery({
    endpoint: `/application/${applicationId}/demand-estimate`,
    tags: [APPLICATION_DETAILS, DEMAND_ESTIMATE],
  });

export const useGetPendingTaxInvoiceApprovalForOtherUtilityQuery = () =>
  useGetQuery({
    endpoint: "/other-utility-application/pending-tax-invoice-approval",
    tags: [APPLICATION_DETAILS],
  });

export const useLazyGetPendingDemandEstimateApprovalApplicationsData = () => {
  const [getDemandEstimation, data] = useLazyGetQuery();
  const getDemandEstimationData = async (query = {}) =>
    await getDemandEstimation(
      {
        endpoint: "/power/application/pending-demand-estimate-approval",
        query,
        tags: [DEMAND_ESTIMATE, APPLICATION_DETAILS],
      },
      true
    ).unwrap();
  return [getDemandEstimationData, data];
};

export const useLazyGetPendingDemandEstimateApprovalForOtherUtilityApplicationsData =
  () => {
    const [getDemandEstimation, data] = useLazyGetQuery();
    const getDemandEstimationDataForOtherUtility = async (query = {}) =>
      await getDemandEstimation(
        {
          endpoint:
            "/other-utility-application/pending-demand-estimate-approval",
          query,
          tags: [DEMAND_ESTIMATE, APPLICATION_DETAILS],
        },
        true
      ).unwrap();
    return [getDemandEstimationDataForOtherUtility, data];
  };

export const usePutTaxInvoiceApprovalData = () => {
  const [updateTaxInvoice, data] = usePutMutation();
  const updateTaxInvoiceData = async (payload, query = {}) =>
    await updateTaxInvoice({
      endpoint: "/demand-estimate/approve-tax-invoice",
      query,
      payload: payload || {},
      toastMessage: "Tax invoice approved successfully",
      tags: [APPLICATION_DETAILS],
    }).unwrap();
  return [updateTaxInvoiceData, data];
};

export const useLazyGetDemandEstimationByApplicationIdQuery = () => {
  const [getDemandEstimation, data] = useLazyGetQuery();
  const getDemandEstimationByApplicationId = async (
    applicationId,
    query = {}
  ) =>
    await getDemandEstimation(
      {
        endpoint: `/application/${applicationId}/demand-estimate`,
        query,
        tags: [DEMAND_ESTIMATE, APPLICATION_DETAILS],
      },
      true
    ).unwrap();
  return [getDemandEstimationByApplicationId, data];
};

export const useGetDemandEstimationByDemandEstimateIdQuery = () => {
  const [getDemandEstimation, data] = useLazyGetQuery();
  const getDemandEstimationByApplicationId = async (
    demandEstimateId,
    query = {}
  ) =>
    await getDemandEstimation(
      {
        endpoint: `/demand-estimate/${demandEstimateId}`,
        query,
        tags: [DEMAND_ESTIMATE, APPLICATION_DETAILS],
      },
      true
    ).unwrap();
  return [getDemandEstimationByApplicationId, data];
};

export const usePostDemandEstimationPaymentMutation = () => {
  const [demandEstimationPayment, data] = usePostMutation();
  const paymentForDemandEstimation = async (payload = {}) => {
    return await demandEstimationPayment({
      endpoint: "/demand-estimate/payment",
      payload,
      tags: [APPLICATION_DETAILS, DEMAND_ESTIMATE, OTHER_APPLICATION_DETAILS],
    }).unwrap();
  };
  return [paymentForDemandEstimation, data];
};

export const usePutUploadDemandEstimationDocumentMutation = () => {
  const [uploadDemandEstimation, data] = usePutMutation();
  const uploadDemandEstimationDocument = async (demandEstimateId, payload) => {
    return await uploadDemandEstimation({
      endpoint: `/demand-estimate/${demandEstimateId}/approved-demand-estimate`,
      payload,
      toastMessage: "Demand estimation uploaded successfully",
      tags: [APPLICATION_DETAILS],
    }).unwrap();
  };
  return [uploadDemandEstimationDocument, data];
};

export const usePutDemandEstimationDocumentApproveMutation = () => {
  const [updateDemandEstimation, data] = usePutMutation();
  const updateDemandEstimationData = async (payload, query = {}) =>
    await updateDemandEstimation({
      endpoint: "/demand-estimate/approve-demand-estimate",
      query,
      payload: payload || {},
      toastMessage: "Demand estimation document status updated successfully",
      tags: [DEMAND_ESTIMATE, APPLICATION_DETAILS],
    }).unwrap();
  return [updateDemandEstimationData, data];
};

export const usePutSettlementChargeDocumentApproveMutation = () => {
  const [SettlementCHargeMutation, data] = usePutMutation();
  const SettlementChargeData = async (applicationId) =>
    await SettlementCHargeMutation({
      endpoint: `/other-utility-application/${applicationId}/approve-settlement-connection`,
      toastMessage: "Settlement Charge document status updated successfully",
      tags: [DEMAND_ESTIMATE, APPLICATION_DETAILS],
    }).unwrap();
  return [SettlementChargeData, data];
};

export const usePostSettlementChargeMutation = () => {
  const [SettlementCHargeMutation, data] = usePostMutation();
  const SettlementChargeData = async (demandEstimateId, payload = []) =>
    await SettlementCHargeMutation({
      endpoint: `/${demandEstimateId}/settlement-connection`,
      payload,
      toastMessage: "Settlement Charges Added successfully",
      tags: [DEMAND_ESTIMATE, APPLICATION_DETAILS, TECHNICAL_SANCTION],
    }).unwrap();
  return [SettlementChargeData, data];
};

export const usePostRefundCalculationPaymentMutation = () => {
  const [refundCalculationPayment, data] = usePostMutation();
  const paymentForRefundCalculation = async (query = {}) => {
    return await refundCalculationPayment({
      endpoint: "/removal-of-connection/payment-url",
      query,
      tags: [],
    }).unwrap();
  };
  return [paymentForRefundCalculation, data];
};

export const useReOpenExpiredApplicationMutation = () => {
  const [reOpenExpiredApplication, data] = usePutMutation();
  const reOpenApplication = async (applicationId, query = {}) =>
    await reOpenExpiredApplication({
      endpoint: `/other-utility-application/${applicationId}/reopen-request`,
      query,
      toastMessage: "Aplication reopen request sent successfully",
      tags: [OTHER_APPLICATION_DETAILS],
    });
  return [reOpenApplication, data];
};

export const useApproveReOpenApplicationMutation = () => {
  const [approveReOpenApplication, data] = usePutMutation();

  const approveReOpen = async (applicationId, query = {}) =>
    await approveReOpenApplication({
      endpoint: `/other-utility-application/${applicationId}/reopen-request/approve`,
      query,
      toastMessage: "Application reopen request approved successfully",
      tags: [OTHER_APPLICATION_DETAILS, APPLICATION_DETAILS],
    });

  return [approveReOpen, data];
};
